import { router, useNavigation } from 'expo-router'
import { useEffect, type PropsWithChildren } from 'react'
import { Modal, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useMediaQuery } from 'react-responsive'
import { useEvent } from 'tamagui'
import { create } from 'zustand'

export function useIsBenefitScreenPresentedAsModal() {
  return !useMediaQuery({ maxWidth: 720 })
}

const useVisible = create<{
  visible: Record<string, boolean>
  setVisible: (screenName: string, visible: boolean) => void
}>((set, get) => ({
  visible: {},
  setVisible: (screenName: string, visible: boolean) =>
    set({ visible: { ...get().visible, [screenName]: visible } }),
}))

function dismissToCardBenefits() {
  router.dismissTo('/main/card-benefits')
}

export function useDismissBenefitModal(screenName: string) {
  const isBenefitScreenPresentedAsModal = useIsBenefitScreenPresentedAsModal()
  return useEvent(() => {
    useVisible.getState().setVisible(screenName, false)
    setTimeout(dismissToCardBenefits, isBenefitScreenPresentedAsModal ? 222 : 0)
  })
}

/** this only modalizes the benefit screen on wider viewports (specifically when {@link isBenefitScreenPresentedAsModal}) */
export function BenefitModal({
  children,
  screenName,
}: PropsWithChildren & {
  screenName: string
}) {
  const isBenefitScreenPresentedAsModal = useIsBenefitScreenPresentedAsModal()
  const navigationState = useNavigation().getState()
  const activeScreenName = navigationState?.routes[navigationState.index]?.name
  const thisScreenIsActive = activeScreenName === screenName
  const { visible, setVisible } = useVisible()
  const initializeModalVisibility = useEvent(() =>
    setVisible(screenName, isBenefitScreenPresentedAsModal),
  )

  useEffect(() => {
    thisScreenIsActive ? initializeModalVisibility() : setVisible(screenName, false)
  }, [
    thisScreenIsActive,
    isBenefitScreenPresentedAsModal,
    screenName,
    setVisible,
    initializeModalVisibility,
  ])

  useEffect(() => initializeModalVisibility(), [initializeModalVisibility])

  const dismissBenefitModal = useDismissBenefitModal(screenName)

  return isBenefitScreenPresentedAsModal ? (
    <Modal visible={visible[screenName]} transparent animationType="fade">
      <TouchableOpacity
        activeOpacity={1}
        style={[{ backgroundColor: '#00000050', flex: 1 }, StyleSheet.absoluteFill]}
        onPress={dismissBenefitModal}
      />
      <View
        style={{
          margin: 'auto',
          flexBasis: 'auto',
          alignItems: 'center',
          justifyContent: 'center',
          marginHorizontal: 'auto',
          width: 720,
          maxWidth: '88%',
          maxHeight: '88%',
          borderRadius: 16,
          overflow: 'hidden',
        }}
      >
        {children}
      </View>
    </Modal>
  ) : (
    children
  )
}
